import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'
import { linkReferral } from '../../lib/referrals'
import { ChevronRight, ChevronLeft, Plus, Trash2, Check, Crown, Lock } from 'lucide-react'
import FallingPetals from '../../components/FallingPetals'

const EVENT_TYPES = ['White Wedding','Traditional / Engagement','Reception','Bridal Shower',"Bachelor's Eve",'Introduction','Thanksgiving','Other']
const STEPS = ['Your Role','Wedding Details','Your Events','Almost Done']

export default function OnboardingPage() {
  const { user, refreshProfile } = useAuth()
  const navigate = useNavigate()
  const [step, setStep]     = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  // Step 0
  const [myRole, setMyRole] = useState('')

  // Step 1 — wedding details
  const [partner1Name, setPartner1Name] = useState('')
  const [partner1Surname, setPartner1Surname] = useState('')
  const [partner2Name, setPartner2Name] = useState('')
  const [partner2Surname, setPartner2Surname] = useState('')
  const [weddingDate, setWeddingDate] = useState('')
  const [venue, setVenue]   = useState('')
  const [city, setCity]     = useState('')
  const [slug, setSlug]     = useState('')

  // (Step 2 — photos removed)

  // Step 3 — events
  const [events, setEvents] = useState([{ type:'White Wedding', date:'', venue:'' }])

  // Step 4 — partner invite
  const [partnerPhone, setPartnerPhone]   = useState('')
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteSent, setInviteSent]     = useState(false)

  function detectCurrency() {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    if (/Africa\/(Lagos|Kano|Abuja|Calabar|Port_Harcourt|Benin|Onitsha)/.test(tz)) return 'NGN'
    if (/Africa\/(Accra|Abidjan|Bamako|Dakar|Freetown|Conakry|Bissau|Monrovia|Lome|Ouagadougou|Niamey)/.test(tz)) return 'GHS'
    if (/Africa\/(Nairobi|Kampala|Dar_es_Salaam|Addis_Ababa|Mogadishu|Djibouti|Kigali|Bujumbura)/.test(tz)) return 'KES'
    if (/Africa\/(Johannesburg|Harare|Maputo|Blantyre|Lusaka|Windhoek|Gaborone|Maseru)/.test(tz)) return 'ZAR'
    if (/^Africa\//.test(tz)) return 'NGN'
    if (/Europe\/(London|Dublin|Lisbon|Guernsey|Jersey|Isle_of_Man)/.test(tz)) return 'GBP'
    if (/^Europe\//.test(tz)) return 'EUR'
    if (/^America\//.test(tz)) return 'USD'
    return 'NGN'
  }

  function generateSlug(p1, p2) {
    const clean = s => s.toLowerCase().replace(/[^a-z]/g,'')
    if (p1 || p2) setSlug(`${clean(p1||'partner1')}-${clean(p2||'partner2')}-${Math.random().toString(36).slice(2,6)}`)
  }

  function addEvent() {
    setEvents([...events, { type:'Traditional / Engagement', date:'', venue:'' }])
  }
  function removeEvent(i) { setEvents(events.filter((_,idx)=>idx!==i)) }
  function updateEvent(i, field, val) {
    const u=[...events]; u[i][field]=val; setEvents(u)
  }

  function canNext() {
    if (step===0) return !!myRole
    if (step===1) return partner1Name.trim()&&partner2Name.trim()
    return true
  }

  async function handleFinish() {
    setSaving(true); setError('')
    try {
      // Update profile name (safe — profile row already exists from auth trigger)
      await supabase.from('profiles').update({
        full_name: partner1Name + ' ' + (partner1Surname || '').trim()
      }).eq('id', user.id)

      // Check if this user already has a wedding (prevent duplicates on retry)
      const { data: existingWedding } = await supabase
        .from('weddings')
        .select('id')
        .or(`partner1_id.eq.${user.id},partner2_id.eq.${user.id}`)
        .limit(1)
      
      if (existingWedding?.length > 0) {
        // Wedding already exists from a previous attempt — go straight to dashboard
        window.location.href = '/dashboard'
        return
      }

      // Generate fresh slug to avoid collisions
      const freshSlug = slug.replace(/-[a-z0-9]{4}$/, '') + '-' + Math.random().toString(36).slice(2,6)
      setSlug(freshSlug)
      const { data: wedding, error: wErr } = await supabase.from('weddings').insert({
        partner1_id: user.id,
        partner1_name: partner1Name,
        partner1_surname: partner1Surname||null,
        partner2_name: partner2Name,
        partner2_surname: partner2Surname||null,
        wedding_date: weddingDate||null,
        venue: venue||null, city: city||null, slug: freshSlug,
        plan: 'free', partner1_role: myRole, referral_code: freshSlug,
        currency: detectCurrency(),
      }).select().single()
      if (wErr) throw wErr

      // Link referral if came from a referral link
      await linkReferral(user.id, wedding.id)

      // Create events
      if (events.length>0) {
        await supabase.from('wedding_events').insert(
          events.map(ev=>({ wedding_id:wedding.id, type:ev.type, date:ev.date||null, venue:ev.venue||null }))
        )
      }

      // Update profile
      await supabase.from('profiles').upsert({ id:user.id, full_name:`${partner1Name} ${partner1Surname}`.trim(), onboarded:true, plan:'free' })

      // Partner invite — generate token, send via WhatsApp
      if (partnerPhone.trim() && !inviteSent) {
        const { data: invite } = await supabase
          .from('partner_invites')
          .insert({ wedding_id:wedding.id, partner_phone:partnerPhone.trim() })
          .select('token').single()
        if (invite?.token) {
          const base    = import.meta.env.VITE_APP_URL || window.location.origin
          const joinUrl = `${base}/join/${invite.token}`
          let phone = partnerPhone.replace(/\D/g,'')
          if (phone.startsWith('0')) phone = '234' + phone.slice(1)
          else if (!phone.startsWith('234') && phone.length <= 10) phone = '234' + phone
          const p1  = partner1Name?.split(' ')[0] || 'Your partner'
          const msg = `Hi! 🎊 ${p1} has set up your wedding on Okoiya and is inviting you to co-plan everything together.\n\nTap the link below to join:\n${joinUrl}\n\nYou'll be asked to create a free account. Welcome to the team 💛`
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank')
          setInviteSent(true)
        }
      }

      await refreshProfile()
      window.location.href = '/dashboard'
    } catch(err) {
      setError(err.message||'Something went wrong. Please try again.')
    } finally { setSaving(false) }
  }

  const progressPct = ((step+1)/STEPS.length)*100
  const isFree = true // always free at onboarding

  // partner1 is the person setting up (their role from step 1); partner2 is the partner.
  // Labels follow each person's role so a bride filling it in sees "Bride's…" for herself.
  const youRole     = myRole === 'Bride' ? 'Bride' : 'Groom'
  const partnerRole = youRole === 'Groom' ? 'Bride' : 'Groom'
  const phFirst = (role) => role === 'Groom' ? 'e.g. Emeka' : 'e.g. Adaeze'
  const phLast  = (role) => role === 'Groom' ? 'e.g. Okonkwo' : 'e.g. Nwosu'

  return (
    <div style={{ minHeight:'100vh', position:'relative', overflow:'hidden', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'1.5rem 1rem' }}>
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg,#F5EDE0,#FAF0E8,#EDE8F5,#E8F0F5,#F5EDE0)', backgroundSize:'400% 400%', animation:'gradientShift 12s ease infinite', zIndex:0 }}/>
      <FallingPetals/>
      <style>{`@keyframes gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}`}</style>
      <div style={{ marginBottom:'2rem', textAlign:'center' }}>
        <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:48, display:'inline-block' }}/>
      </div>

      <div className="card" style={{ width:'100%', maxWidth:520, padding:'2rem', position:'relative', zIndex:1 }}>
        {/* Progress */}
        <div style={{ marginBottom:'1.75rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.4rem' }}>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', fontWeight:600 }}>Step {step+1} of {STEPS.length} — {STEPS[step]}</span>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#C4826A', fontWeight:700 }}>{Math.round(progressPct)}%</span>
          </div>
          <div style={{ height:4, background:'#E5E0D8', borderRadius:100 }}>
            <div style={{ height:'100%', width:`${progressPct}%`, background:'linear-gradient(to right,#9BAF9B,#C4826A)', borderRadius:100, transition:'width 0.4s ease' }}/>
          </div>
        </div>

        {/* STEP 0 — Role */}
        {step===0 && (
          <div className="fade-up">
            <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', marginBottom:'0.4rem' }}>Welcome! What's your role?</h2>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', marginBottom:'1.5rem' }}>This helps us personalise your experience.</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
              {['Bride','Groom'].map(role=>(
                <button key={role} onClick={()=>setMyRole(role)} style={{ padding:'1rem', border:`2px solid ${myRole===role?'#C4826A':'#E5E0D8'}`, background:myRole===role?'rgba(196,130,106,0.08)':'white', borderRadius:'0.875rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.9rem', color:myRole===role?'#C4826A':'#1A1A1A', transition:'all 0.2s' }}>{role}</button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 1 — Wedding Details */}
        {step===1 && (
          <div className="fade-up">
            <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', marginBottom:'0.4rem' }}>Tell us about your wedding</h2>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', marginBottom:'1.5rem' }}>Sets up your profile and mini-site.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.625rem', alignItems:'end' }}>
                <div><label className="label">{youRole}'s First Name&nbsp;*</label><input className="input-field" placeholder={phFirst(youRole)} value={partner1Name} onChange={e=>{ setPartner1Name(e.target.value); generateSlug(e.target.value,partner2Name) }}/></div>
                <div><label className="label">{youRole}'s Surname</label><input className="input-field" placeholder={phLast(youRole)} value={partner1Surname} onChange={e=>setPartner1Surname(e.target.value)}/></div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.625rem', alignItems:'end' }}>
                <div><label className="label">{partnerRole}'s First Name&nbsp;*</label><input className="input-field" placeholder={phFirst(partnerRole)} value={partner2Name} onChange={e=>{ setPartner2Name(e.target.value); generateSlug(partner1Name,e.target.value) }}/></div>
                <div><label className="label">{partnerRole}'s Surname</label><input className="input-field" placeholder={phLast(partnerRole)} value={partner2Surname} onChange={e=>setPartner2Surname(e.target.value)}/></div>
              </div>
              <div><label className="label">Primary Wedding Date</label><input className="input-field" type="date" value={weddingDate} onChange={e=>setWeddingDate(e.target.value)}/></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.625rem' }}>
                <div><label className="label">Main Venue</label><input className="input-field" placeholder="e.g. The Grand Ballroom" value={venue} onChange={e=>setVenue(e.target.value)}/></div>
                <div><label className="label">City / State</label><input className="input-field" placeholder="e.g. Lagos" value={city} onChange={e=>setCity(e.target.value)}/></div>
              </div>
              {slug&&<div style={{ background:'white', borderRadius:'0.625rem', padding:'0.5rem 0.875rem' }}>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B' }}>Your wedding link: </span>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#C4826A', fontWeight:600 }}>{window.location.origin}/w/{slug}</span>
              </div>}
            </div>
          </div>
        )}

        {/* STEP 2 — Photos */}
        {step===2 && (
          <div className="fade-up">
            <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', marginBottom:'0.4rem' }}>Your wedding events</h2>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', marginBottom:'1.5rem' }}>Add each celebration — ceremony, reception, traditional, etc.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
              {events.map((ev,i)=>(
                <div key={i} style={{ background:'#FFFFFF', border:'1px solid #E5E0D8', borderRadius:'0.875rem', padding:'1rem' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.625rem' }}>
                    <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#C4826A' }}>Event {i+1}</span>
                    {events.length>1&&<button onClick={()=>removeEvent(i)} style={{ background:'none', border:'none', cursor:'pointer', color:'#BCBCBC' }}><Trash2 size={14}/></button>}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
                    <select className="input-field" value={ev.type} onChange={e=>updateEvent(i,'type',e.target.value)}>
                      {EVENT_TYPES.map(t=><option key={t}>{t}</option>)}
                    </select>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem' }}>
                      <input className="input-field" type="date" value={ev.date} onChange={e=>updateEvent(i,'date',e.target.value)}/>
                      <input className="input-field" placeholder="Venue" value={ev.venue} onChange={e=>updateEvent(i,'venue',e.target.value)}/>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add event — locked for free users after 1 */}
              {events.length < 1 || isFree ? (
                events.length >= 1 && isFree ? (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.75rem 1rem', background:'rgba(196,130,106,0.04)', border:'1px dashed rgba(196,130,106,0.25)', borderRadius:'0.875rem' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      <Lock size={13} color="#C4826A"/>
                      <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B' }}>Add more events with Premium</span>
                    </div>
                    <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', fontWeight:700, color:'#C4826A', background:'rgba(196,130,106,0.1)', borderRadius:100, padding:'2px 8px', display:'flex', alignItems:'center', gap:'0.25rem' }}>
                      <Crown size={11}/> Premium
                    </span>
                  </div>
                ) : (
                  <button onClick={addEvent} className="btn-secondary" style={{ display:'flex', alignItems:'center', gap:'0.4rem', justifyContent:'center' }}>
                    <Plus size={15}/> Add Another Event
                  </button>
                )
              ) : (
                <button onClick={addEvent} className="btn-secondary" style={{ display:'flex', alignItems:'center', gap:'0.4rem', justifyContent:'center' }}>
                  <Plus size={15}/> Add Another Event
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP 4 — Partner Invite */}
        {step===3 && (
          <div className="fade-up">
            <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', marginBottom:'0.4rem' }}>Invite your partner</h2>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', marginBottom:'1.5rem', lineHeight:1.65 }}>Optional — they can create their own account and co-manage everything with you. You can also do this later in Settings.</p>
            {!inviteSent ? (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
                <div>
                  <label className="label">Partner's WhatsApp Number</label>
                  <input className="input-field" type="tel" placeholder="e.g. 08012345678" value={partnerPhone} onChange={e=>setPartnerPhone(e.target.value)}/>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.35rem 0 0' }}>A WhatsApp message with a join link will open on your phone.</p>
                </div>
              </div>
            ) : (
              <div style={{ background:'rgba(155,175,155,0.1)', border:'1px solid rgba(155,175,155,0.3)', borderRadius:'0.875rem', padding:'1rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                <Check size={16} color="#9BAF9B"/>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#7A917A', fontWeight:600 }}>WhatsApp opened - send the message to your partner!</span>
              </div>
            )}
          </div>
        )}

        {error&&<p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#C4826A', marginTop:'1rem', textAlign:'center' }}>{error}</p>}

        {/* Navigation */}
        <div style={{ display:'flex', gap:'0.75rem', marginTop:'2rem', justifyContent:'space-between' }}>
          {step>0
            ? <button onClick={()=>setStep(s=>s-1)} className="btn-secondary" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}><ChevronLeft size={15}/> Back</button>
            : <div/>}
          {step < STEPS.length-1
            ? <button onClick={()=>setStep(s=>s+1)} className="btn-primary" disabled={!canNext()} style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginLeft:'auto' }}>
                Next <ChevronRight size={15}/>
              </button>
            : <button onClick={handleFinish} disabled={saving} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginLeft:'auto' }}>
                {saving?'Setting up…':'Launch my wedding ✦'}
              </button>}
        </div>
      </div>
    </div>
  )
}
